/**
 * Single source of truth for all URLs, email addresses and domain-tied
 * identifiers used across the app.
 *
 * - Per-environment values are read from `process.env.REACT_APP_*` here (and
 *   ONLY here) so a rebrand/domain change touches the env files, not the tree.
 * - Values that never vary per environment are kept as constants below.
 *
 * When adding a new URL/email/id anywhere in the app, add it here and import it
 * rather than hardcoding a literal or reading `process.env` directly.
 *
 * IMPORTANT: env vars MUST be referenced as static `process.env.REACT_APP_*`
 * member expressions. The Vite build (see `envPlugin` in vite.config.ts) and
 * CRA/webpack before it inline these by textual replacement at build time — a
 * dynamic/computed access like `process.env[key]` is NOT replaced and resolves
 * to `undefined` in the browser bundle.
 */

import { isMittiBrand } from "./brand";

/**
 * Base domains. These are the primary knobs for the SafetyCulture rebrand —
 * change a host here and every URL composed from it updates.
 */
export const domains = isMittiBrand
  ? ({
      care: "https://insurance.mitti.com",
      safetyculture: "https://mitti.com",
      brokerEntsiaUrl: "https://broker.insurance.mitti.com",
    } as const)
  : ({
      care: "https://care.safetyculture.com",
      safetyculture: "https://safetyculture.com",
      brokerEntsiaUrl: "https://broker.care.safetyculture.com",
    } as const);

export const urls = {
  // Same-origin API path. The app is always served behind a proxy that forwards
  // /api to the api-care backend — the Cloudflare worker in deployed envs, the
  // Vite dev-server proxy locally (see vite.config.ts). Kept relative so it
  // follows whatever domain the app is served from (care.safetyculture.com or
  // insurance.mitti.com) with no per-env env var / secret. [FG-7325]
  apiBaseUrl: "/api",
  // Env-backed (per-environment — existing env var keys, do not rename)
  oauthGrantUrl: process.env.REACT_APP_SAFETYCULTURE_OAUTH_GRANT_URL ?? "",
  iauditorLink: process.env.REACT_APP_IAUDITOR_LINK ?? "",
  salesforceQuotesUrl: process.env.REACT_APP_SALESFORCE_QUOTES_URL ?? "",
  salesforceNewQuoteUrl: process.env.REACT_APP_SALESFORCE_NEWQUOTE_URL ?? "",
  calendlyVraLink: process.env.REACT_APP_CALENDLY_VRA_LINK ?? "",
  // Not imported anywhere in app code. The REACT_APP_SAFETYCULTURE_ASSETS_URL env
  // var is consumed DIRECTLY by index.html (favicon, via build-time %VAR%
  // substitution — see htmlPlugin in vite.config.ts), which can't import this
  // module. Kept here so the env var stays documented in the single source of
  // truth; do not remove without also updating index.html.
  safetycultureAssetsUrl: process.env.REACT_APP_SAFETYCULTURE_ASSETS_URL ?? "",

  // Static (moved from hardcoded component literals), composed from `domains`
  careHome: `${domains.care}/`,
  careContact: `${domains.care}/contact`,
  careContactTrailingSlash: `${domains.care}/contact/`,
  careTerms: `${domains.care}/terms-and-conditions`,
  careTermsTrailingSlash: `${domains.care}/terms-and-conditions/`,
  carePrivacy: `${domains.care}/privacy-policy/`,
  scLegalTerms: `${domains.safetyculture}/legal/terms-and-conditions/`,
  scLegalPrivacy: `${domains.safetyculture}/legal/privacy-policy/`,
  publicLibrarySearch:
    "https://public-library.safetyculture.io/search/?q=mitti&page=1t",
  mittiClaims: "https://www.mitti.com.au/claims",
  googleMapsQuery: "https://maps.google.com/?q=", // callers append encoded address
  youtubeEmbed: "https://www.youtube.com/embed/", // callers append video id
  brokerLoginLink: domains.brokerEntsiaUrl,
} as const;

/** OAuth logout base, derived from the grant URL (e.g. `.../v2/logout`). */
export const oauthLogoutBase = urls.oauthGrantUrl.replace("/authorize", "");

export const emails = isMittiBrand
  ? ({
      careSupport: "support.ins.au@mitti.com",
      careClaims: "claims@mitti.com",
    } as const)
  : ({
      careSupport: process.env.REACT_APP_CARE_SUPPORT_EMAIL ?? "",
      careClaims: process.env.REACT_APP_CARE_CLAIMS_EMAIL ?? "",
    } as const);

export const ids = {
  oauthClientId: process.env.REACT_APP_SAFETYCULTURE_OAUTH_CLIENT_ID ?? "",
  // NOTE: the Google Tag Manager container ID (GTM-TDGZ277) lives only in
  // index.html — it can't import this module and nothing in-app reads it here,
  // so it is intentionally not duplicated in this config.
} as const;
